import { useEffect, useState } from "react";
import { ArrowLeft, Clock, MessageCircle } from "lucide-react";
import BalaoModelo from "./BalaoModelo";
import CampanhaTopo from "./CampanhaTopo";
import CelularWhatsApp from "./CelularWhatsApp";
import EnviarCampanha from "./EnviarCampanha";
import EscolherModelo from "./EscolherModelo";
import PublicoWhatsApp, { totalDoPublico } from "./PublicoWhatsApp";
import {
  LINK_EXEMPLO, PRECO_MSG, camposIniciais, nomeAmigavel, ordenarModelos, partesDoModelo,
  problemaDoRascunho, rascunhoParaMeta, valorDoCampo, waApi,
} from "./modelos";

function Passo({ n, titulo, children, apagado }) {
  return (
    <section className={`rounded-2xl border bg-card p-5 ${apagado ? "opacity-50 pointer-events-none" : ""}`}>
      <h4 className="mb-4 flex items-center gap-3 font-semibold">
        <span className="grid h-7 w-7 place-items-center rounded-full bg-[#25d366] text-sm font-bold text-white">{n}</span>
        {titulo}
      </h4>
      {children}
    </section>
  );
}

// Próximo horário que o agendamento aceita (9h–22h, no futuro, de 15 em 15 min).
function proximoHorario() {
  const d = new Date(Date.now() + 30 * 60000);
  let hora = d.getHours(); let min = Math.ceil(d.getMinutes() / 15) * 15;
  if (min === 60) { hora += 1; min = 0; }
  if (hora < 9) { hora = 9; min = 0; }
  if (hora > 21) { d.setDate(d.getDate() + 1); hora = 10; min = 0; }
  const data = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  return { data, hora: String(hora).padStart(2, "0"), min: String(min).padStart(2, "0") };
}

const RASCUNHO_VAZIO = { titulo: "", texto: "", botoes: [{ tipo: "link", texto: "Ver cardápio" }] };
// Ordem da Meta: botões de ação juntos (link, ligar), respostas rápidas juntas.
const ORDEM_BOTAO = { link: 0, ligar: 1, resposta: 2 };

/**
 * Página da campanha de WhatsApp (não é popup — pedido do dono, por espaço):
 * à esquerda os passos (mensagem → quem recebe → enviar), à direita o celular
 * com a conversa do WhatsApp mostrando a mensagem como o cliente vê.
 */
export default function NovaCampanhaWhatsApp({ modoInicial = "prontos", onVoltar }) {
  const [modo, setModo] = useState(modoInicial);
  const [modelos, setModelos] = useState(null);
  const [escolhido, setEscolhido] = useState(null);
  const [campos, setCampos] = useState([]);
  const [rascunho, setRascunho] = useState(RASCUNHO_VAZIO);
  const [enviandoModelo, setEnviandoModelo] = useState(false);
  const [avisoModelo, setAvisoModelo] = useState(null);
  const [publico, setPublico] = useState(null);
  const [selecionados, setSelecionados] = useState(null); // null = Todos
  const [saldo, setSaldo] = useState(null);
  const [loja, setLoja] = useState({ logo: "", telefone: "" });
  const [nomeCampanha, setNomeCampanha] = useState("");
  const [quando, setQuando] = useState("agora");
  const [agenda, setAgenda] = useState(proximoHorario);

  const carregarModelos = () =>
    waApi.modelos().then((l) => setModelos(ordenarModelos(l.filter((t) => t.category === "MARKETING")))).catch(() => setModelos([]));

  useEffect(() => {
    carregarModelos();
    waApi.publico().then(setPublico).catch(() => setPublico({ total_alvo: 0, por_segmento: [] }));
    waApi.carteira().then((c) => setSaldo(c?.saldo_equivalente_centavos ?? null)).catch(() => {});
    waApi.empresa().then((e) => setLoja({ logo: e?.logo_url || "", telefone: e?.phone || "" })).catch(() => {});
  }, []);

  const exemplo = { nome: publico?.exemplo_nome || "Maria", empresa: publico?.nome_loja || "" };
  const partes = escolhido ? partesDoModelo(escolhido) : null;

  const escolher = (m) => {
    setEscolhido(m); setCampos(camposIniciais(m));
    if (!nomeCampanha.trim()) setNomeCampanha(nomeAmigavel(m.name));
  };

  const enviarModelo = async () => {
    // Sem nome próprio, o modelo leva o nome da campanha.
    const r0 = { ...rascunho, titulo: rascunho.titulo || nomeCampanha };
    const problema = problemaDoRascunho(r0);
    if (problema) { setAvisoModelo({ tipo: "erro", txt: problema }); return; }
    setEnviandoModelo(true); setAvisoModelo(null);
    try {
      const r = await waApi.criarModelo(rascunhoParaMeta(r0, { ...exemplo, link: `https://${LINK_EXEMPLO}` }));
      if (r?.success) {
        setAvisoModelo({ tipo: "ok", txt: "Enviado! A Meta está analisando — ele aparece em Modelos prontos." });
        setRascunho(RASCUNHO_VAZIO);
        carregarModelos();
      } else {
        setAvisoModelo({ tipo: "erro", txt: r?.erro || "A Meta não aceitou o modelo." });
      }
    } catch {
      setAvisoModelo({ tipo: "erro", txt: "Sem conexão com o servidor." });
    } finally { setEnviandoModelo(false); }
  };

  const total = totalDoPublico(publico, selecionados);
  const custo = total * (publico?.preco_unitario ?? PRECO_MSG);
  const faltaSaldo = !publico?.sem_custo && saldo != null && custo * 100 > saldo;
  const camposOk = campos.every((c) => c.tipo !== "texto" || c.texto.trim());
  const usandoPronto = modo === "prontos" && !!escolhido;
  const pedido = escolhido && {
    template: escolhido.name, idioma: escolhido.language || "pt_BR", params: campos.map(valorDoCampo),
    url_dinamica: partes.botoes.some((b) => b.dinamico), corpo: partes.corpo,
    segmentos: selecionados, nome: nomeCampanha,
    agendada_para: quando === "agendar" ? `${agenda.data} ${agenda.hora}:${agenda.min}` : null,
  };
  const botoesRascunho = [
    ...[...rascunho.botoes].sort((a, b) => ORDEM_BOTAO[a.tipo] - ORDEM_BOTAO[b.tipo]),
    { tipo: "resposta", texto: "Parar promoções" },
  ];

  return (
    <div className="p-4 sm:p-6 space-y-5 w-full">
      <div>
        <button type="button" onClick={onVoltar}
          className="mb-3 inline-flex items-center gap-1.5 text-sm font-medium text-muted-foreground hover:text-foreground">
          <ArrowLeft className="h-4 w-4" /> Disparos em massa
        </button>
        <h1 className="flex items-center gap-2 text-2xl sm:text-3xl font-bold">
          <MessageCircle className="h-7 w-7 text-[#25d366]" /> Nova campanha de WhatsApp
        </h1>
        <p className="text-muted-foreground">Escolha ou escreva a mensagem, marque quem recebe e dispare. O celular mostra como o cliente vê.</p>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_380px] gap-6">
        <div className="space-y-5 min-w-0">
          <Passo n={1} titulo="Campanha">
            <CampanhaTopo nome={nomeCampanha} setNome={setNomeCampanha} quando={quando} setQuando={setQuando}
              agenda={agenda} setAgenda={setAgenda} />
          </Passo>
          <Passo n={2} titulo="Mensagem">
            <EscolherModelo modo={modo} setModo={setModo} modelos={modelos} escolhido={escolhido} onEscolher={escolher}
              campos={campos} setCampos={setCampos}
              editor={{ rascunho, setRascunho, onEnviar: enviarModelo, enviando: enviandoModelo, aviso: avisoModelo,
                telefoneLoja: loja.telefone, nomePadrao: nomeCampanha }} />
          </Passo>
          {modo === "criar" ? (
            <div className="flex items-start gap-3 rounded-2xl border border-dashed p-5 text-sm text-muted-foreground">
              <Clock className="h-5 w-5 shrink-0 text-amber-500" />
              <p>Depois que a Meta aprovar, a sua mensagem aparece em <b className="text-foreground">Modelos prontos</b>. Aí é só escolher, marcar quem recebe e disparar.</p>
            </div>
          ) : (
            <>
              <Passo n={3} titulo="Quem vai receber" apagado={!usandoPronto}>
                <PublicoWhatsApp publico={publico} selecionados={selecionados} setSelecionados={setSelecionados} saldoCentavos={saldo} />
              </Passo>
              <Passo n={4} titulo="Enviar" apagado={!usandoPronto}>
                <EnviarCampanha pedido={pedido} total={total} custo={custo} faltaSaldo={faltaSaldo} pronto={usandoPronto && camposOk}
                  onConcluida={onVoltar} />
              </Passo>
            </>
          )}
        </div>

        <div className="xl:sticky xl:top-4 self-start space-y-3">
          <CelularWhatsApp nomeLoja={exemplo.empresa} logo={loja.logo}>
            {modo === "criar" ? (
              <BalaoModelo partes={{ corpo: rascunho.texto, botoes: botoesRascunho }} exemplo={exemplo} />
            ) : escolhido ? (
              <BalaoModelo partes={partes} campos={campos} exemplo={exemplo} />
            ) : (
              <p className="mx-auto mt-24 max-w-[220px] rounded-lg bg-white/90 px-3 py-2 text-center text-[12px] text-[#54656f] shadow-sm">
                Escolha um modelo ao lado pra ver como ele chega no WhatsApp do cliente.
              </p>
            )}
          </CelularWhatsApp>
          <p className="text-center text-xs text-muted-foreground">
            Em <span className="rounded bg-[#d9fdd3] px-1 font-medium text-[#0a7d5a]">verde</span>, o que o sistema preenche:
            o nome vem do cadastro de cada cliente (ex.: {exemplo.nome}).
          </p>
        </div>
      </div>
    </div>
  );
}
